import { useState, useEffect, useRef } from 'react'
import { WifiOff, AlertCircle, RefreshCw, Loader2 } from 'lucide-react'
import { useNetworkStatus } from '@/hooks/useNetworkStatus'
import { Button } from '@/components/ui/button'

export function OfflineErrorState() {
  const { isOnline } = useNetworkStatus()
  const [reconnecting, setReconnecting] = useState(false)
  const prevOnlineRef = useRef(isOnline)

  useEffect(() => {
    const wasOffline = !prevOnlineRef.current
    prevOnlineRef.current = isOnline

    if (isOnline && wasOffline) {
      // Network just restored — show "Reconnecting..." while the hook reloads data.
      // If the parent clears the error (data arrived), this component unmounts before
      // the timeout fires. If data never arrives, fall through to "Failed to load".
      setReconnecting(true)
      const timer = setTimeout(() => setReconnecting(false), 3000)
      return () => clearTimeout(timer)
    }

    if (!isOnline) setReconnecting(false)
  }, [isOnline])

  if (reconnecting) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-2 text-center">
        <Loader2 className="h-10 w-10 animate-spin text-muted-foreground/60" />
        <p className="text-sm font-medium">Reconnecting...</p>
        <p className="text-xs text-muted-foreground">Loading your data, please wait.</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center justify-center py-20 gap-2 text-center">
      {isOnline ? (
        <AlertCircle className="h-10 w-10 text-destructive/60" />
      ) : (
        <WifiOff className="h-10 w-10 text-muted-foreground" />
      )}
      <p className="text-sm font-medium">
        {isOnline ? 'Failed to load' : "You're offline"}
      </p>
      <p className="text-xs text-muted-foreground">
        {isOnline
          ? 'Something went wrong. Please try again.'
          : "This content isn't cached yet. Connect to the internet to load it."}
      </p>
      <Button
        variant="outline"
        size="sm"
        className="mt-2 gap-1.5"
        disabled={!isOnline}
        onClick={() => window.location.reload()}
      >
        <RefreshCw className="h-3.5 w-3.5" />
        Refresh
      </Button>
    </div>
  )
}
